// NextSessionCard — the student dashboard's soonest-upcoming-session card.
import Badge from "../shared/Badge.js";
import { IconCalendarEvent, IconExternalLink, IconMapPin } from "../shared/icons.js";
import { formatFullDateTime } from "../../lib/formatDateTime.js";
import type { SessionWithCourse } from "@dm-lms/shared/types";

interface NextSessionCardProps {
  session: SessionWithCourse | null;
  isLoading: boolean;
}

export default function NextSessionCard({ session, isLoading }: NextSessionCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-dm-navy">
        <IconCalendarEvent width={16} height={16} className="text-dm-slate" />
        Next session
      </h2>

      {isLoading && <p className="text-sm text-dm-slate">Loading…</p>}

      {!isLoading && !session && <p className="text-sm text-dm-slate">No upcoming sessions.</p>}

      {!isLoading && session && (
        <div>
          <p className="text-[15px] font-semibold text-dm-navy">{session.title}</p>
          <p className="text-[13px] text-dm-slate">{session.courseTitle}</p>

          <p className="mt-2 text-sm text-dm-navy">{formatFullDateTime(session.scheduledAt)}</p>

          <div className="mt-2 flex items-center gap-2">
            <Badge tone="gray">{session.mode}</Badge>
            <span className="text-xs text-dm-slate">{session.durationMinutes} minutes</span>
          </div>

          <div className="mt-3">
            {(session.mode === "virtual" || session.mode === "hybrid") && session.meetingUrl && (
              <a
                href={session.meetingUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-dm-blue hover:underline"
              >
                Join session
                <IconExternalLink width={14} height={14} />
              </a>
            )}
            {session.mode === "physical" && session.venue && (
              <p className="inline-flex items-center gap-1.5 text-sm text-dm-navy">
                <IconMapPin width={14} height={14} className="text-dm-slate" />
                {session.venue}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
